import Axios from 'axios';
import * as NewsRequest from '@/models/newsRequest';
import * as NewsResponse from '@/models/newsResponse';

export type NewsDetail = NewsResponse.NewsResponse;

export interface SearchCondition {
  keyWord: string;
  perPage: number;
  page: number;
  sort: string;
}

interface State {
  loading: boolean;
  countAll: number;
  newsList: NewsDetail[];
  searchCondition: SearchCondition;
  selectedNews: NewsDetail | null;
}
const mapState: State = {
  loading: false,
  countAll: 0,
  newsList: new Array<NewsDetail>(),
  searchCondition: {
    keyWord: '',
    perPage: 20,
    page: 1,
    sort: '',
  },
  selectedNews: null,
};

const mapActions = {
  async searchNews({ dispatch, commit }: any, searchCondition: SearchCondition) {
    commit('setLoading', true);
    const data = await dispatch('getNewsList', searchCondition).catch((error: any) => {
      commit('setLoading', false);
      throw error;
    });
    commit('searchNewsList', data);
  },
  async getNewsList({ commit }: any, searchCondition: SearchCondition) {
    const request: NewsRequest.NewsRequest = {
      newsId: undefined,
      publicationDate: undefined,
      title: undefined,
      note: undefined,
      keyWord: searchCondition.keyWord,
      perPage: searchCondition.perPage,
      page: searchCondition.page,
      sort: searchCondition.sort,
    };
    const response = await Axios.post<NewsResponse.NewsListResponse>('/api/News/SearchList', request).catch((error) => {
      throw error;
    });
    response.data.newsList.forEach((news) => {
      if (typeof news.publicationDate === 'string') {
        news.publicationDate = new Date(news.publicationDate + 'Z');
      }
      if (typeof news.updateDate === 'string') {
        news.updateDate = new Date(news.updateDate + 'Z');
      }
    });
    return response.data;
  },
  async selectNews({ dispatch, commit }: any, newsId: string) {
    commit('setLoading', true);
    const data = await dispatch('getNewsDetails', newsId).catch(
      (error: any) => {
        commit('setLoading', false);
        throw error;
      }
    );
    commit('selectNews', data);
  },
  async getNewsDetails({ commit }: any, newsId: string) {
    const request: NewsRequest.NewsRequest = {
      newsId,
      publicationDate: undefined,
      title: undefined,
      note: undefined,
      keyWord: undefined,
      perPage: undefined,
      page: undefined,
      sort: undefined
    };
    const response = await Axios.post<NewsResponse.NewsResponse>('/api/News/Detail', request).catch(
      (error) => {
        throw error;
      }
    );
    if (typeof response.data.publicationDate === 'string') {
      response.data.publicationDate = new Date(response.data.publicationDate + 'Z');
    }
    if (typeof response.data.updateDate === 'string') {
      response.data.updateDate = new Date(response.data.updateDate + 'Z');
    }
    return response.data;
  },
  async createNews({ commit }: any, newsInfo: NewsDetail) {
    if (!newsInfo || !newsInfo.title) {
      throw new Error('タイトルを入力してください。');
    }
    if (!newsInfo.note) {
      throw new Error('内容を入力してください。');
    }
    const request: NewsRequest.NewsRequest = {
      newsId: undefined,
      publicationDate: newsInfo.publicationDate,
      title: newsInfo.title,
      note: newsInfo.note,
      keyWord: undefined,
      perPage: undefined,
      page: undefined,
      sort: undefined,
    };
    const response = await Axios.post('/api/News/Create', request).catch((error) => {
      throw new Error('お知らせの登録に失敗しました。');
    });
    return response.data;
  },
  async editNews({ commit }: any, newsInfo: NewsDetail) {
    if (!newsInfo || !newsInfo.title) {
      throw new Error('タイトルを入力してください。');
    }
    if (!newsInfo.note) {
      throw new Error('内容を入力してください。');
    }
    const request: NewsRequest.NewsRequest = {
      newsId: newsInfo.id,
      publicationDate: newsInfo.publicationDate,
      title: newsInfo.title,
      note: newsInfo.note,
      keyWord: undefined,
      perPage: undefined,
      page: undefined,
      sort: undefined,
    };
    const response = await Axios.post('/api/News/Edit', request).catch((error) => {
      throw new Error('お知らせの変更に失敗しました。');
    });
    commit('changeNews', newsInfo);
    return response.data;
  },
  async deleteNews({ commit }: any, newsId: string) {
    if (!newsId) {
      throw new Error('削除するお知らせを選択してください。');
    }
    const request: NewsRequest.NewsRequest = {
      newsId,
      publicationDate: undefined,
      title: undefined,
      note: undefined,
      keyWord: undefined,
      perPage: undefined,
      page: undefined,
      sort: undefined,
    };
    const response = await Axios.post('/api/News/Delete', request).catch((error) => {
      throw new Error('お知らせの削除に失敗しました。');
    });
    commit('removeNews', newsId);
    return response.data;
  },
  async getNews({ commit }: any, newsId: string) {
    const request: NewsRequest.NewsRequest = {
      newsId,
      publicationDate: undefined,
      title: undefined,
      note: undefined,
      keyWord: undefined,
      perPage: undefined,
      page: undefined,
      sort: undefined,
    };
    const response = await Axios.post('/api/News/Detail', request).catch((error) => {
      throw error;
    });
    return response.data as NewsDetail;
  },
};

const mapMutations = {
  setLoading(state: State, loading: boolean) {
    state.loading = loading;
  },
  searchNewsList(state: State, data: NewsResponse.NewsListResponse) {
    state.countAll = data.countAll;
    state.newsList = data.newsList;
    state.loading = false;
  },
  selectNews(state: State, data: NewsResponse.NewsResponse) {
    state.selectedNews = data;
    state.loading = false;
  },
  changeNews(state: State, newsInfo: NewsDetail) {
    const findIndex = state.newsList.findIndex((value) => {
      return (value.id === newsInfo.id);
    });
    if (findIndex >= 0) {
      state.newsList.splice(findIndex, 1, newsInfo);
    }
  },
  removeNews(state: State, id: string) {
    const findIndex = state.newsList.findIndex((value) => {
      return (value.id === id);
    });
    if (findIndex >= 0) {
      state.newsList.splice(findIndex, 1);
    }
  },
  initSelectedNews(state: State) {
    state.selectedNews = {
      id: '',
      publicationDate: null as any,
      title: '',
      note: '',
      updateDate: null as any,
    };
  }
};

export default {
  namespaced: true,
  state: mapState,
  actions: mapActions,
  mutations: mapMutations,
};
